import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChevronDown, Plus } from "lucide-react";
import API from "../services/api";
import MarketSheet from "../components/MarketSheet";
import { getSuggestedSession } from "../utils/marketStatus";

const SUBMIT_SOUND = "/sounds/humordome-magic-button-click-453258.mp3";

const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT" },
  { id: 2, name: "MADHUR NIGHT" },
  { id: 3, name: "MILAN NIGHT" },
  { id: 4, name: "ANMOL BAZAR" },
  { id: 5, name: "KALYAN NIGHT" },
  { id: 6, name: "RAJDHANI NIGHT" },
  { id: 7, name: "MAIN BAZAR" },
  { id: 8, name: "VANDE BHARAT" },
  { id: 9, name: "KALYAN" },
  { id: 10, name: "RAJDHANI DAY" },
  { id: 11, name: "MILAN DAY" },
  { id: 12, name: "SADAR BAZAR" },
  { id: 13, name: "MADHUR DAY" },
  { id: 14, name: "TIME BAZAR" },
  { id: 15, name: "SRIDEVI" },
  { id: 16, name: "MILAN MORNING" },
  { id: 17, name: "KANTARA" },
];

const themes = {
  violet: {
    headerLine: "linear-gradient(90deg, #7c3aed, #9333ea, #c026d3)",
    pillBorder: "#ddd6fe",
    pillBg: "linear-gradient(90deg, #f5f3ff, #faf5ff)",
    pillText: "#6d28d9",
    selectorBg: "linear-gradient(135deg, #eff6ff, #eef2ff)",
    selectorBorder: "#dbeafe",
    label: "#1e3a8a",
    selectBorder: "#bfdbfe",
    accent: "#2563eb",
    total: "#2563eb",
    primary: "linear-gradient(90deg, #93c5fd, #a78bfa)",
  },
  orange: {
    headerLine: "linear-gradient(90deg, #f59e0b, #f97316, #fb7185)",
    pillBorder: "#fde68a",
    pillBg: "linear-gradient(90deg, #fffbeb, #fff7ed)",
    pillText: "#b45309",
    selectorBg: "linear-gradient(135deg, #fff7ed, #fffbeb)",
    selectorBorder: "#fde68a",
    label: "#92400e",
    selectBorder: "#fcd34d",
    accent: "#d97706",
    total: "#ea580c",
    primary: "linear-gradient(90deg, #fdba74, #fdc66f)",
  },
};

const DIGIT_OPTIONS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const variants = {
  single_bulk: {
    title: "SINGLE BULK",
    theme: "violet",
    mode: "digitBulk",
    numbers: DIGIT_OPTIONS,
    amountLabel: "Enter Amount",
    typePrefix: "single_bulk",
  },
  jodi: {
    title: "JODI",
    theme: "violet",
    mode: "pairAmountGrid",
    numbers: Array.from({ length: 100 }, (_, index) => String(index).padStart(2, "0")),
    amountLabel: "Enter Amount",
    typePrefix: "jodi",
    forceSession: "OPEN",
  },
  jodi_bulk: {
    title: "JODI BULK",
    theme: "violet",
    mode: "pairBulk",
    numbers: Array.from({ length: 100 }, (_, index) => String(index).padStart(2, "0")),
    amountLabel: "Enter Amount",
    typePrefix: "jodi_bulk",
    forceSession: "OPEN",
  },
  red_bracket: {
    title: "RED BRACKET",
    theme: "orange",
    mode: "redBracketSet",
    numbers: ["00", "05", "11", "16", "22", "27", "33", "38", "44", "49", "50", "55", "61", "66", "72", "77", "83", "88", "94", "99"],
    amountLabel: "Enter Amount",
    typePrefix: "red_bracket",
    forceSession: "OPEN",
  },
  sp: {
    title: "SINGLE PATTI",
    theme: "violet",
    mode: "pattiList",
    pattiLabel: "Enter Patti",
    addLabel: "Add Bid to List",
    typePrefix: "sp",
  },
  sp_bulk: {
    title: "SP BULK",
    theme: "violet",
    mode: "baseBulk",
    numbers: DIGIT_OPTIONS,
    amountLabel: "Enter Amount",
    helper: "Select Base (12 SP numbers each)",
    typePrefix: "sp_bulk",
  },
  sp_motor: {
    title: "SP MOTOR",
    theme: "violet",
    mode: "motor",
    digitsLabel: "SP Motor Digits",
    digitsHelper: "(Min 4 unique digits)",
    amountLabel: "Bid Amount (per number)",
    generatedLabel: "Single Patti Bids",
    minDigits: 4,
    typePrefix: "sp_motor",
  },
  dp: {
    title: "DOUBLE PATTI",
    theme: "orange",
    mode: "pattiList",
    pattiLabel: "Enter Patti",
    addLabel: "Add Bid to List",
    typePrefix: "dp",
  },
  dp_bulk: {
    title: "DP BULK",
    theme: "orange",
    mode: "baseBulk",
    numbers: DIGIT_OPTIONS,
    amountLabel: "Enter Amount",
    helper: "Select Base (9 DP numbers each)",
    typePrefix: "dp_bulk",
  },
  dp_motor: {
    title: "DP MOTOR",
    theme: "orange",
    mode: "motor",
    digitsLabel: "DP Motor Digits",
    digitsHelper: "(Min 4 unique digits)",
    amountLabel: "Bid Amount (per number)",
    generatedLabel: "Double Patti Bids",
    minDigits: 4,
    typePrefix: "dp_motor",
  },
  tp: {
    title: "TRIPLE PATTI",
    theme: "orange",
    mode: "tripleAmountGrid",
    numbers: ["000", "111", "222", "333", "444", "555", "666", "777", "888", "999"],
    amountLabel: "Enter Amount",
    typePrefix: "tp",
  },
  half_sangam: {
    title: "HALF SANGAM",
    theme: "violet",
    mode: "halfSangam",
    typePrefix: "half_sangam",
    forceSession: "CLOSE",
  },
  full_sangam: {
    title: "FULL SANGAM",
    theme: "violet",
    mode: "fullSangam",
    typePrefix: "full_sangam",
    forceSession: "CLOSE",
  },
};

function GameBetPage({ variant }) {
  const config = variants[variant] || variants.single_bulk;
  const theme = themes[config.theme];
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(sessionStorage.getItem("selectedMarketId") || "1");
  const [session, setSession] = useState("CLOSE");
  const [pointAmount, setPointAmount] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [gridAmounts, setGridAmounts] = useState({});
  const [patti, setPatti] = useState("");
  const [pattiError, setPattiError] = useState("");
  const [entryAmount, setEntryAmount] = useState("");
  const [entries, setEntries] = useState([]);
  const [motorDigits, setMotorDigits] = useState("");
  const [motorAmount, setMotorAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [limits, setLimits] = useState({ min_bet: "10", max_bet: "10000" });
  const [ankStatus, setAnkStatus] = useState({ ank_limit: null, used_digits: [], used_count: 0, remaining: null });
  const [marketSheetOpen, setMarketSheetOpen] = useState(false);
  const [sangamErrors, setSangamErrors] = useState({});
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sangam, setSangam] = useState({
    openPana: "",
    closeDigit: "",
    openDigit: "",
    closePana: "",
    closePanaFull: "",
  });
  const placingRef = useRef(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    API.get("/markets")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const normalRows = rows.filter((market) => (market.market_type || "normal") !== "starline");
        setMarkets(normalRows.length ? normalRows : fallbackMarkets);
      })
      .catch(() => setMarkets(fallbackMarkets));
    API.get("/settings/public")
      .then((res) => setLimits((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const targetSession = config.forceSession || session;
    if (!selectedMarket || !targetSession) return;
    API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(targetSession)}`)
      .then((res) => setAnkStatus(res.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null }))
      .catch(() => {});
  }, [config.forceSession, selectedMarket, session, user?.id]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const marketOptions = markets.length ? markets : fallbackMarkets;
  const selectedMarketInfo = marketOptions.find((market) => String(market.id) === String(selectedMarket)) || marketOptions[0];
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" }).toUpperCase();
  const amountValue = Number(pointAmount || 0);

  const generatedMotorEntries = useMemo(() => {
    const uniqueDigits = Array.from(new Set((motorDigits || "").replace(/\D/g, "").split(""))).sort();
    if (config.mode !== "motor" || uniqueDigits.length < (config.minDigits || 0)) return [];

    if (variant === "sp_motor") {
      return SINGLE_PATTI_OPTIONS.filter((option) =>
        option.split("").every((digit) => uniqueDigits.includes(digit))
      );
    }

    if (variant === "dp_motor") {
      return DOUBLE_PATTI_OPTIONS.filter((option) =>
        new Set(option.split("")).size === 2 &&
        Array.from(new Set(option.split(""))).every((digit) => uniqueDigits.includes(digit))
      );
    }

    return [];
  }, [config.minDigits, config.mode, motorDigits, variant]);

  const total = useMemo(() => {
    if (config.typePrefix === "sp_bulk") {
      return selectedNumbers.length * 12 * Number(pointAmount || 0);
    }

    if (config.typePrefix === "dp_bulk") {
      return selectedNumbers.length * 9 * Number(pointAmount || 0);
    }

    if (["digitBulk", "baseBulk", "pairBulk", "redBracketSet"].includes(config.mode)) {
      return selectedNumbers.length * Number(pointAmount || 0);
    }

    if (config.mode === "pairAmountGrid") {
      return Object.values(gridAmounts).reduce((sum, value) => sum + Number(value || 0), 0);
    }

    if (config.mode === "tripleAmountGrid") {
      return Object.values(gridAmounts).reduce((sum, value) => sum + Number(value || 0), 0);
    }

    return entries.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [config.mode, config.typePrefix, entries, gridAmounts, pointAmount, selectedNumbers.length]);
  const draftEntries = useMemo(() => {
    const amount = Number(entryAmount || 0);
    if (amount <= 0) return [];

    if (["pattiList", "pairList"].includes(config.mode)) {
      const requiredLength = config.mode === "pairList" ? 2 : 3;
      if (!new RegExp(`^\\d{${requiredLength}}$`).test(patti)) return [];
      if (config.mode === "pattiList" && validatePattiValue(config.typePrefix, patti)) return [];
      return [{ number: patti, amount }];
    }

    if (config.mode === "halfSangam") {
      const openCombo = /^\d{3}$/.test(sangam.openPana) && /^\d$/.test(sangam.closeDigit);
      const closeCombo = /^\d$/.test(sangam.openDigit) && /^\d{3}$/.test(sangam.closePana);
      if (openCombo && !validatePannaOption(sangam.openPana)) {
        return [{ number: `${sangam.openPana}-${sangam.closeDigit}`, amount }];
      }
      if (closeCombo && !validatePannaOption(sangam.closePana)) {
        return [{ number: `${sangam.openDigit}-${sangam.closePana}`, amount }];
      }
      return [];
    }

    if (config.mode === "fullSangam") {
      if (/^\d{3}$/.test(sangam.openPana) && /^\d{3}$/.test(sangam.closePanaFull) && !validatePannaOption(sangam.openPana) && !validatePannaOption(sangam.closePanaFull)) {
        return [{ number: `${sangam.openPana}-${sangam.closePanaFull}`, amount }];
      }
      return [];
    }

    if (config.mode === "motor" && generatedMotorEntries.length && Number(motorAmount || 0) > 0) {
      return generatedMotorEntries.map((number) => ({ number, amount: Number(motorAmount) }));
    }

    return [];
  }, [config.mode, config.typePrefix, entryAmount, generatedMotorEntries, motorAmount, patti, sangam.closeDigit, sangam.closePana, sangam.closePanaFull, sangam.openDigit, sangam.openPana]);
  const effectiveTotal = total + draftEntries.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const pattiDigit = useMemo(() => {
    const digits = String(patti || "").replace(/\D/g, "").slice(0, 3).split("").map(Number);
    if (!digits.length) return "";
    return String(digits.reduce((sum, digit) => sum + digit, 0) % 10);
  }, [patti]);
  const validPattiOptions = useMemo(() => {
    if (config.typePrefix === "sp") return SINGLE_PATTI_OPTIONS;
    if (config.typePrefix === "dp") return DOUBLE_PATTI_OPTIONS;
    return [];
  }, [config.typePrefix]);
  const pattiSuggestions = useMemo(() => {
    if (!patti || !validPattiOptions.length) return [];
    return validPattiOptions.filter((option) => option.startsWith(patti)).slice(0, 24);
  }, [patti, validPattiOptions]);
  const showPattiNoResult = useMemo(() => {
    if (!patti || !validPattiOptions.length) return false;
    return pattiSuggestions.length === 0;
  }, [patti, pattiSuggestions.length, validPattiOptions.length]);
  const bulkPreviewEntries = useMemo(() => {
    if (!selectedNumbers.length) return [];
    if (config.typePrefix === "sp_bulk") {
      return selectedNumbers.flatMap((number) =>
        getSinglePattisForDigit(number).map((pana) => ({ number: pana, amount: amountValue }))
      );
    }
    if (config.typePrefix === "dp_bulk") {
      return selectedNumbers.flatMap((number) =>
        getDoublePattisForDigit(number).map((pana) => ({ number: pana, amount: amountValue }))
      );
    }
    return [];
  }, [amountValue, config.typePrefix, selectedNumbers]);
  const sangamSuggestions = useMemo(() => ({
    openPana: getPannaSuggestions(sangam.openPana),
    closePana: getPannaSuggestions(sangam.closePana),
    closePanaFull: getPannaSuggestions(sangam.closePanaFull),
  }), [sangam.closePana, sangam.closePanaFull, sangam.openPana]);
  const halfSangamOpenSelected = Boolean(sangam.openPana || sangam.closeDigit);
  const halfSangamCloseSelected = Boolean(sangam.openDigit || sangam.closePana);
  const bulkNeedsAmountFirst = ["sp_bulk", "dp_bulk"].includes(config.typePrefix);
  const isCompact = viewportWidth <= 430;
  const isTiny = viewportWidth <= 360;
  const isDesktop = viewportWidth >= 900;
  const playSubmitSound = () => {
    try {
      const audio = new Audio(SUBMIT_SOUND);
      audio.volume = 0.72;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch {}
  };
  const isAnkMode = config.typePrefix === "single_bulk";
  const usedDigitSet = useMemo(() => new Set((ankStatus.used_digits || []).map((value) => String(value))), [ankStatus.used_digits]);
  const ankLimitReached = Number.isFinite(Number(ankStatus.ank_limit)) && Number(ankStatus.ank_limit) > 0 && Number(ankStatus.remaining || 0) <= 0;
  const getSelectedNewAnkCount = (nextSelected = selectedNumbers) =>
    Array.from(new Set(nextSelected.map((value) => String(value))))
      .filter((value) => !usedDigitSet.has(value)).length;
  const isDigitOptionDisabled = (value) => {
    if (!isAnkMode || selectedNumbers.includes(value)) return false;
    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    return limit > 0 && !usedDigitSet.has(String(value)) && getSelectedNewAnkCount() >= remaining;
  };

  const handleMarketChange = (value) => {
    sessionStorage.setItem("selectedMarketId", String(value));
    setSelectedMarket(value);
  };

  useEffect(() => {
    if (config.forceSession || !selectedMarketInfo) return;
    setSession(getSuggestedSession(selectedMarketInfo));
  }, [config.forceSession, selectedMarketInfo]);

  const toggleNumber = (value) => {
    if (isDigitOptionDisabled(value)) {
      alert(`Ank limit reached. You can use only ${ankStatus.ank_limit} digits in this market and session.`);
      return;
    }
    if (bulkNeedsAmountFirst && Number(pointAmount || 0) <= 0) {
      alert("Enter amount first");
      return;
    }
    setSelectedNumbers((current) => {
      if (current.includes(value)) return current.filter((item) => item !== value);
      const next = [...current, value];
      const limit = Number(ankStatus.ank_limit || 0);
      const remaining = Number(ankStatus.remaining || 0);
      if (isAnkMode && limit > 0 && !usedDigitSet.has(String(value)) && getSelectedNewAnkCount(next) > remaining) {
        alert(`Ank limit reached. You can use only ${ankStatus.ank_limit} digits in this market and session.`);
        return current;
      }
      return next;
    });
  };

  const handleGridAmount = (number, value) => {
    setGridAmounts((current) => ({
      ...current,
      [number]: value.replace(/[^\d]/g, ""),
    }));
  };

  const addManualEntry = () => {
    const requiredLength = config.mode === "pairList" ? 2 : 3;
    if (!new RegExp(`^\\d{${requiredLength}}$`).test(patti) || Number(entryAmount) <= 0) {
      setPattiError("");
      alert(`Enter a valid ${requiredLength}-digit number and amount`);
      return;
    }

    const pattiError = validatePattiValue(config.typePrefix, patti);
    if (pattiError) {
      setPattiError(pattiError);
      return;
    }

    setPattiError("");
    setEntries((current) => [...current, { number: patti, amount: Number(entryAmount) }]);
    setPatti("");
    setEntryAmount("");
  };

  const updateSangam = (key, value, maxLength) => {
    const nextValue = value.replace(/\D/g, "").slice(0, maxLength);
    setSangam((current) => ({ ...current, [key]: nextValue }));
    setSangamErrors((current) => ({
      ...current,
      [key]: maxLength === 3 && nextValue.length === 3 ? validatePannaOption(nextValue) : "",
    }));
  };

  const addHalfSangamEntry = () => {
    const amount = Number(entryAmount || 0);
    const openCombo = /^\d{3}$/.test(sangam.openPana) && /^\d$/.test(sangam.closeDigit);
    const closeCombo = /^\d$/.test(sangam.openDigit) && /^\d{3}$/.test(sangam.closePana);
    const openPanaError = sangam.openPana.length === 3 ? validatePannaOption(sangam.openPana) : "";
    const closePanaError = sangam.closePana.length === 3 ? validatePannaOption(sangam.closePana) : "";
    if (openPanaError || closePanaError) {
      setSangamErrors((current) => ({ ...current, openPana: openPanaError, closePana: closePanaError }));
      return;
    }
    if ((!openCombo && !closeCombo) || amount <= 0) {
      alert("Enter one Half Sangam combination and amount");
      return;
    }
    const number = openCombo ? `${sangam.openPana}-${sangam.closeDigit}` : `${sangam.openDigit}-${sangam.closePana}`;
    setEntries((current) => [...current, { number, amount }]);
    setSangamErrors({});
    setSangam((current) => ({ ...current, openPana: "", closeDigit: "", openDigit: "", closePana: "" }));
    setEntryAmount("");
  };

  const addFullSangamEntry = () => {
    const amount = Number(entryAmount || 0);
    const openPanaError = sangam.openPana.length === 3 ? validatePannaOption(sangam.openPana) : "";
    const closePanaError = sangam.closePanaFull.length === 3 ? validatePannaOption(sangam.closePanaFull) : "";
    if (openPanaError || closePanaError) {
      setSangamErrors((current) => ({ ...current, openPana: openPanaError, closePanaFull: closePanaError }));
      return;
    }
    if (!/^\d{3}$/.test(sangam.openPana) || !/^\d{3}$/.test(sangam.closePanaFull) || amount <= 0) {
      alert("Enter open panna, close panna and amount");
      return;
    }
    setEntries((current) => [...current, { number: `${sangam.openPana}-${sangam.closePanaFull}`, amount }]);
    setSangamErrors({});
    setSangam((current) => ({ ...current, openPana: "", closePanaFull: "" }));
    setEntryAmount("");
  };

  const addMotorEntries = () => {
    if (!generatedMotorEntries.length || Number(motorAmount) <= 0) {
      alert("Enter valid digits and amount");
      return;
    }

    setEntries((current) => [
      ...current,
      ...generatedMotorEntries.map((number) => ({ number, amount: Number(motorAmount) })),
    ]);
    setMotorDigits("");
    setMotorAmount("");
  };

  const placeBets = async () => {
    if (placingRef.current || placing) return;
    if (!user?.id) {
      navigate("/login");
      return;
    }

    let payload = [];
    const minBet = Number(limits.min_bet || 10);
    const maxBet = Number(limits.max_bet || 10000);

    if (["digitBulk", "baseBulk", "pairBulk", "redBracketSet"].includes(config.mode)) {
      if (!selectedNumbers.length || amountValue <= 0) {
        alert("Select numbers and enter amount");
        return;
      }

      if (isAnkMode) {
        const limit = Number(ankStatus.ank_limit || 0);
        const remaining = Number(ankStatus.remaining || 0);
        if (limit > 0 && getSelectedNewAnkCount() > remaining) {
          alert(`Ank limit is ${limit} for this market and ${(config.forceSession || session)} session. Remove extra ank before submit.`);
          return;
        }
      }

      if (amountValue < minBet || amountValue > maxBet) {
        alert(`Each bet must be between Rs ${minBet} and Rs ${maxBet}`);
        return;
      }

      if (config.typePrefix === "sp_bulk") {
        payload = selectedNumbers.flatMap((number) =>
          getSinglePattisForDigit(number).map((pattiNumber) => ({
            user_id: user.id,
            market_id: selectedMarket,
            number: pattiNumber,
            amount: amountValue,
            type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
          }))
        );
      } else if (config.typePrefix === "dp_bulk") {
        payload = selectedNumbers.flatMap((number) =>
          getDoublePattisForDigit(number).map((pattiNumber) => ({
            user_id: user.id,
            market_id: selectedMarket,
            number: pattiNumber,
            amount: amountValue,
            type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
          }))
        );
      } else {
        payload = selectedNumbers.map((number) => ({
          user_id: user.id,
          market_id: selectedMarket,
          number,
          amount: amountValue,
          type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
        }));
      }
    } else if (config.mode === "pairAmountGrid" || config.mode === "tripleAmountGrid") {
      const selectedEntries = Object.entries(gridAmounts).filter(([, value]) => Number(value) > 0);
      if (!selectedEntries.length) {
        alert(config.mode === "tripleAmountGrid" ? "Enter amount for at least one triple patti" : "Enter amount for at least one jodi");
        return;
      }

      const invalidEntry = selectedEntries.find(([, value]) => Number(value || 0) < minBet || Number(value || 0) > maxBet);
      if (invalidEntry) {
        alert(`Each bet must be between Rs ${minBet} and Rs ${maxBet}`);
        return;
      }

      payload = selectedEntries.map(([number, value]) => ({
        user_id: user.id,
        market_id: selectedMarket,
        number,
        amount: Number(value),
        type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
      }));
    } else {
      const finalEntries = [...entries, ...draftEntries];
      if (!finalEntries.length) {
        alert("Add at least one bid to the list");
        return;
      }

      const invalidEntry = finalEntries.find((entry) => Number(entry.amount || 0) < minBet || Number(entry.amount || 0) > maxBet);
      if (invalidEntry) {
        alert(`Each bet must be between Rs ${minBet} and Rs ${maxBet}`);
        return;
      }

      payload = finalEntries.map((entry) => ({
        user_id: user.id,
        market_id: selectedMarket,
        number: entry.number,
        amount: Number(entry.amount),
        type: `${config.typePrefix}-${(config.forceSession || session).toLowerCase()}`,
      }));
    }

    const uniquePayload = [];
    const seenBets = new Set();
    for (const item of payload) {
      const key = `${item.market_id}|${item.number}|${item.type}|${item.time_slot || ""}`;
      if (seenBets.has(key)) continue;
      seenBets.add(key);
      uniquePayload.push(item);
    }

    placingRef.current = true;
    setPlacing(true);
    try {
      for (const item of uniquePayload) {
        await API.post("/place-bet", item);
      }
      if (user?.id) {
        const targetSession = config.forceSession || session;
        const statusRes = await API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(targetSession)}`);
        setAnkStatus(statusRes.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null });
      }
      setEntries([]);
      setSelectedNumbers([]);
      setGridAmounts({});
      setPatti("");
      setEntryAmount("");
      setMotorDigits("");
      setMotorAmount("");
      setSangam({ openPana: "", closeDigit: "", openDigit: "", closePana: "", closePanaFull: "" });
      setSangamErrors({});
      setPattiError("");
      setPointAmount("");
      playSubmitSound();
      alert("Bet placed successfully");
      navigate("/game-select");
    } catch (err) {
      alert(err?.response?.data?.message || "Error placing bet");
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  };

  return (
    <div style={styles.page}>
      <header style={{ ...styles.topBar, ...(isDesktop ? styles.topBarDesktop : null) }}>
        <div style={{ ...styles.headerLine, background: theme.headerLine }} />
        <button type="button" style={styles.circleBack} onClick={() => navigate(-1)}>
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
        <h1 style={styles.title}>{config.title}</h1>
        <div style={{ ...styles.datePill, borderColor: theme.pillBorder, background: theme.pillBg, color: theme.pillText }}>
          <CalendarDays size={16} />
          {today}
        </div>
      </header>

      <main style={{ ...styles.main, ...(isDesktop ? styles.mainDesktop : null), padding: isCompact ? "10px 10px 0" : isDesktop ? styles.mainDesktop.padding : styles.main.padding }}>
        <section style={{ ...styles.selectorCard, ...(isDesktop ? styles.selectorCardDesktop : null), background: theme.selectorBg, borderColor: theme.selectorBorder, gridTemplateColumns: config.forceSession || isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))" }}>
          <label style={{ ...styles.fieldLabel, color: theme.label }}>
            Market <span style={styles.required}>*</span>
            <span style={styles.selectWrap}>
              <button
                type="button"
                style={{ ...styles.select, ...styles.selectButton, borderColor: theme.selectBorder }}
                onClick={() => setMarketSheetOpen(true)}
              >
                {selectedMarketInfo?.name || "Select Market"}
              </button>
              <ChevronDown size={18} style={{ ...styles.selectIcon, color: theme.accent }} />
            </span>
          </label>

          {!config.forceSession && <label style={{ ...styles.fieldLabel, color: theme.label }}>
            Session <span style={styles.required}>*</span>
            <span style={styles.selectWrap}>
              <select value={session} onChange={(event) => setSession(event.target.value)} style={{ ...styles.select, borderColor: theme.selectBorder }}>
                <option value="OPEN">OPEN</option>
                <option value="CLOSE">CLOSE</option>
              </select>
              <ChevronDown size={18} style={{ ...styles.selectIcon, color: theme.accent }} />
            </span>
          </label>}
        </section>
        {(["digitBulk", "baseBulk", "pairBulk", "redBracketSet"].includes(config.mode)) && (
          <>
            <section style={styles.whiteCard}>
              <label style={styles.secondaryLabel}>{config.amountLabel}</label>
              <div style={styles.largeInputWrap}>
                <input
                  value={pointAmount}
                  onChange={(event) => setPointAmount(event.target.value.replace(/[^\d]/g, ""))}
                  inputMode="numeric"
                  placeholder="₹"
                  style={styles.largeInput}
                />
              </div>
            </section>

            <section style={{ ...styles.selectorCard, background: theme.selectorBg, borderColor: theme.selectorBorder, marginTop: 16, display: "block" }}>
              {config.mode === "redBracketSet" && (
                <button
                  type="button"
                  style={styles.selectAllRed}
                  onClick={() => {
                    const allSelected = selectedNumbers.length === config.numbers.length;
                    setSelectedNumbers(allSelected ? [] : [...config.numbers]);
                  }}
                >
                  <span style={selectedNumbers.length === config.numbers.length ? styles.selectAllTickActive : styles.selectAllTick} />
                  <span>Select All Red Bracket (20 Jodis)</span>
                </button>
              )}
              {config.helper && <div style={{ ...styles.bulkTitle, color: theme.label }}>{config.helper}</div>}
              {bulkNeedsAmountFirst && Number(pointAmount || 0) <= 0 && (
                <div style={styles.noResultText}>Enter amount first, then select a number</div>
              )}
              {isAnkMode && Number.isFinite(Number(ankStatus.ank_limit)) && Number(ankStatus.ank_limit) > 0 && (
                <div style={styles.ankBanner}>
                  <strong>{`Ank Limit: ${ankStatus.used_count}/${ankStatus.ank_limit}`}</strong>
                  <span>
                    {ankLimitReached
                      ? "Ank limit reached for this market and session. Remaining new digits are disabled."
                      : `${ankStatus.remaining} more digit${Number(ankStatus.remaining) === 1 ? "" : "s"} available in this market/session`}
                  </span>
                </div>
              )}
              <div style={config.mode === "redBracketSet" ? { ...styles.redBracketGrid, gridTemplateColumns: isDesktop ? "repeat(5, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: isCompact ? 6 : styles.redBracketGrid.gap } : { ...styles.numberGrid, gridTemplateColumns: isDesktop ? "repeat(10, minmax(0, 1fr))" : isTiny ? "repeat(4, minmax(0, 1fr))" : styles.numberGrid.gridTemplateColumns, gap: isCompact ? 7 : styles.numberGrid.gap }}>
                {config.numbers.map((number) => {
                  const active = selectedNumbers.includes(number);
                  const disabled = (bulkNeedsAmountFirst && Number(pointAmount || 0) <= 0) || isDigitOptionDisabled(number);
                  return (
                    <button
                      key={number}
                      type="button"
                      disabled={disabled}
                      style={{
                        ...styles.numberButton,
                        borderColor: active ? theme.accent : "#bfdbfe",
                        background: disabled ? "#e5e7eb" : active ? theme.primary : "#cfe0fb",
                        color: disabled ? "#94a3b8" : active ? "#fff" : theme.label,
                        boxShadow: active ? "0 10px 20px rgba(37, 99, 235, 0.22)" : "none",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.8 : 1,
                      }}
                      onClick={() => toggleNumber(number)}
                    >
                      {number}
                    </button>
                  );
                })}
              </div>
            </section>

            {!!bulkPreviewEntries.length && (
              <section style={{ ...styles.selectorCard, background: "#f8fbff", borderColor: "#dbeafe", marginTop: 16, display: "block" }}>
                <div style={styles.previewHeader}>
                  <span>Selected Panas ({bulkPreviewEntries.length})</span>
                  <span>Tap X to remove</span>
                </div>
                <div style={{ ...styles.previewGrid, gridTemplateColumns: isTiny ? "repeat(3, minmax(0, 1fr))" : styles.previewGrid.gridTemplateColumns, gap: isCompact ? 8 : styles.previewGrid.gap }}>
                  {bulkPreviewEntries.map((entry) => (
                    <div key={`${entry.number}-${entry.amount}`} style={styles.previewCard}>
                      <button
                        type="button"
                        style={styles.previewRemove}
                        onClick={() => {
                          const selectedBase = config.typePrefix === "sp_bulk"
                            ? selectedNumbers.find((number) => getSinglePattisForDigit(number).includes(entry.number))
                            : selectedNumbers.find((number) => getDoublePattisForDigit(number).includes(entry.number));
                          if (selectedBase) {
                            setSelectedNumbers((current) => current.filter((item) => item !== selectedBase));
                          }
                        }}
                      >
                        ×
                      </button>
                      <div style={styles.previewNumber}>{entry.number}</div>
                      <div style={styles.previewAmount}>{amountValue > 0 ? `₹${entry.amount}` : "₹0"}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {config.mode === "pairAmountGrid" && (
          <section style={{ ...styles.selectorCard, background: theme.selectorBg, borderColor: theme.selectorBorder, marginTop: 16, display: "block" }}>
            <div style={{ ...styles.jodiAmountGrid, gridTemplateColumns: isDesktop ? "repeat(10, minmax(0, 1fr))" : isCompact ? "repeat(4, minmax(0, 1fr))" : styles.jodiAmountGrid.gridTemplateColumns, gap: isCompact ? 8 : styles.jodiAmountGrid.gap }}>
              {config.numbers.map((number) => (
                <div key={number} style={styles.jodiCell}>
                  <div style={styles.jodiNumber}>{number}</div>
                  <input
                    value={gridAmounts[number] || ""}
                    onChange={(event) => handleGridAmount(number, event.target.value)}
                    inputMode="numeric"
                    placeholder="₹"
                    style={styles.jodiAmountInput}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {config.mode === "tripleAmountGrid" && (
          <section style={{ ...styles.selectorCard, background: theme.selectorBg, borderColor: theme.selectorBorder, marginTop: 16, display: "block" }}>
            <div style={{ ...styles.tripleAmountGrid, gridTemplateColumns: isDesktop ? "repeat(10, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))", gap: isCompact ? 6 : styles.tripleAmountGrid.gap }}>
              {config.numbers.map((number) => (
                <div key={number} style={styles.jodiCell}>
                  <div style={styles.tripleNumber}>{number}</div>
                  <input
                    value={gridAmounts[number] || ""}
                    onChange={(event) => handleGridAmount(number, event.target.value)}
                    inputMode="numeric"
                    placeholder="₹"
                    style={styles.jodiAmountInput}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {["pattiList", "pairList"].includes(config.mode) && (
          <section style={styles.whiteCard}>
            <div style={{ ...styles.formGrid, gridTemplateColumns: isCompact ? "1fr" : styles.formGrid.gridTemplateColumns }}>
              <label style={styles.secondaryLabel}>
                {config.pattiLabel || config.numberLabel} <span style={styles.required}>*</span>
                <input
                  value={patti}
                  onChange={(event) => {
                    const nextValue = event.target.value.replace(/[^\d]/g, "").slice(0, config.mode === "pairList" ? 2 : 3);
                    setPatti(nextValue);
                    setPattiError(nextValue.length === 3 ? validatePattiValue(config.typePrefix, nextValue) : "");
                  }}
                  inputMode="numeric"
                  placeholder={config.placeholder || "000"}
                  style={styles.smallInput}
                />
                {config.mode === "pattiList" && <div style={styles.autoDigit}>Digit: {pattiDigit || "-"}</div>}
                {!!pattiSuggestions.length && (
                  <div style={styles.suggestionList}>
                    {pattiSuggestions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        style={styles.suggestionButton}
                        onClick={() => {
                          setPatti(option);
                          setPattiError("");
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                {showPattiNoResult && <div style={styles.noResultText}>No result found</div>}
                {!!pattiError && <div style={styles.errorText}>{pattiError}</div>}
              </label>

              <label style={styles.secondaryLabel}>
                Enter Amount <span style={styles.required}>*</span>
                <div style={styles.smallInputWrap}>
                  <span style={styles.smallCurrency}>{"\u20B9"}</span>
                  <input
                    value={entryAmount}
                    onChange={(event) => setEntryAmount(event.target.value.replace(/[^\d]/g, ""))}
                    inputMode="numeric"
                    placeholder="0"
                    style={{ ...styles.smallInput, paddingLeft: 42, textAlign: "left" }}
                  />
                </div>
              </label>
            </div>

            <button type="button" style={{ ...styles.addButton, background: theme.primary }} onClick={addManualEntry}>
              <Plus size={18} /> {config.addLabel || "Add Bid to List"}
            </button>

            {!!entries.length && (
              <div style={styles.entryList}>
                {entries.map((entry, index) => (
                  <div key={`${entry.number}-${index}`} style={styles.entryRow}>
                    <span>{entry.number}</span>
                    <span>{`\u20B9${entry.amount}`}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {config.mode === "halfSangam" && (
          <section style={styles.whiteCard}>
            <div style={halfSangamCloseSelected ? { ...styles.sangamBoxDisabled, gridTemplateColumns: isCompact ? "1fr" : styles.sangamBoxDisabled.gridTemplateColumns } : { ...styles.sangamBox, gridTemplateColumns: isCompact ? "1fr" : styles.sangamBox.gridTemplateColumns }}>
              <label style={styles.secondaryLabel}>
                Open Panna
                <input disabled={halfSangamCloseSelected} value={sangam.openPana} onChange={(event) => updateSangam("openPana", event.target.value, 3)} inputMode="numeric" placeholder="000" style={halfSangamCloseSelected ? styles.smallInputDisabled : styles.smallInput} />
                {!!sangam.openPana && !!sangamSuggestions.openPana.length && (
                  <div style={styles.suggestionList}>
                    {sangamSuggestions.openPana.map((option) => (
                      <button key={option} type="button" style={styles.suggestionButton} onClick={() => updateSangam("openPana", option, 3)}>
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                {!!sangam.openPana && !sangamSuggestions.openPana.length && <div style={styles.noResultText}>No result found</div>}
                {!!sangamErrors.openPana && <div style={styles.errorText}>{sangamErrors.openPana}</div>}
              </label>
              <label style={styles.secondaryLabel}>
                Close Number
                <input disabled={halfSangamCloseSelected} value={sangam.closeDigit} onChange={(event) => updateSangam("closeDigit", event.target.value, 1)} inputMode="numeric" placeholder="0" style={halfSangamCloseSelected ? styles.smallInputDisabled : styles.smallInput} />
              </label>
            </div>
            <div style={styles.orLine}>OR</div>
            <div style={halfSangamOpenSelected ? { ...styles.sangamBoxDisabled, gridTemplateColumns: isCompact ? "1fr" : styles.sangamBoxDisabled.gridTemplateColumns } : { ...styles.sangamBox, gridTemplateColumns: isCompact ? "1fr" : styles.sangamBox.gridTemplateColumns }}>
              <label style={styles.secondaryLabel}>
                Open Number
                <input disabled={halfSangamOpenSelected} value={sangam.openDigit} onChange={(event) => updateSangam("openDigit", event.target.value, 1)} inputMode="numeric" placeholder="0" style={halfSangamOpenSelected ? styles.smallInputDisabled : styles.smallInput} />
              </label>
              <label style={styles.secondaryLabel}>
                Close Panna
                <input disabled={halfSangamOpenSelected} value={sangam.closePana} onChange={(event) => updateSangam("closePana", event.target.value, 3)} inputMode="numeric" placeholder="000" style={halfSangamOpenSelected ? styles.smallInputDisabled : styles.smallInput} />
                {!!sangam.closePana && !!sangamSuggestions.closePana.length && (
                  <div style={styles.suggestionList}>
                    {sangamSuggestions.closePana.map((option) => (
                      <button key={option} type="button" style={styles.suggestionButton} onClick={() => updateSangam("closePana", option, 3)}>
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                {!!sangam.closePana && !sangamSuggestions.closePana.length && <div style={styles.noResultText}>No result found</div>}
                {!!sangamErrors.closePana && <div style={styles.errorText}>{sangamErrors.closePana}</div>}
              </label>
            </div>
            <label style={{ ...styles.secondaryLabel, marginTop: 16 }}>
              Bid Amount <span style={styles.required}>*</span>
              <div style={styles.smallInputWrap}>
                <span style={styles.smallCurrency}>{"\u20B9"}</span>
                <input value={entryAmount} onChange={(event) => setEntryAmount(event.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="Enter amount" style={{ ...styles.smallInput, paddingLeft: 42, textAlign: "left" }} />
              </div>
            </label>
            <button type="button" style={{ ...styles.addButton, background: theme.primary }} onClick={addHalfSangamEntry}>
              <Plus size={18} /> Add Bid
            </button>
            {!!entries.length && <EntryList entries={entries} />}
          </section>
        )}

        {config.mode === "fullSangam" && (
          <section style={styles.whiteCard}>
            <label style={styles.secondaryLabel}>
              Open Panna <span style={styles.required}>*</span>
              <input value={sangam.openPana} onChange={(event) => updateSangam("openPana", event.target.value, 3)} inputMode="numeric" placeholder="000" style={styles.fullInputCompact} />
              {!!sangam.openPana && !!sangamSuggestions.openPana.length && (
                <div style={styles.suggestionList}>
                  {sangamSuggestions.openPana.map((option) => (
                    <button key={option} type="button" style={styles.suggestionButton} onClick={() => updateSangam("openPana", option, 3)}>
                      {option}
                    </button>
                  ))}
                </div>
              )}
              {!!sangam.openPana && !sangamSuggestions.openPana.length && <div style={styles.noResultText}>No result found</div>}
              {!!sangamErrors.openPana && <div style={styles.errorText}>{sangamErrors.openPana}</div>}
            </label>
            <label style={{ ...styles.secondaryLabel, marginTop: 16 }}>
              Close Panna <span style={styles.required}>*</span>
              <input value={sangam.closePanaFull} onChange={(event) => updateSangam("closePanaFull", event.target.value, 3)} inputMode="numeric" placeholder="000" style={styles.fullInputCompact} />
              {!!sangam.closePanaFull && !!sangamSuggestions.closePanaFull.length && (
                <div style={styles.suggestionList}>
                  {sangamSuggestions.closePanaFull.map((option) => (
                    <button key={option} type="button" style={styles.suggestionButton} onClick={() => updateSangam("closePanaFull", option, 3)}>
                      {option}
                    </button>
                  ))}
                </div>
              )}
              {!!sangam.closePanaFull && !sangamSuggestions.closePanaFull.length && <div style={styles.noResultText}>No result found</div>}
              {!!sangamErrors.closePanaFull && <div style={styles.errorText}>{sangamErrors.closePanaFull}</div>}
            </label>
            <label style={{ ...styles.secondaryLabel, marginTop: 16 }}>
              Bid Amount <span style={styles.required}>*</span>
              <div style={styles.smallInputWrap}>
                <span style={styles.smallCurrency}>{"\u20B9"}</span>
                <input value={entryAmount} onChange={(event) => setEntryAmount(event.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="Enter amount" style={{ ...styles.smallInput, paddingLeft: 42, textAlign: "left" }} />
              </div>
            </label>
            <button type="button" style={{ ...styles.addButton, background: theme.primary }} onClick={addFullSangamEntry}>
              <Plus size={18} /> Add Bid
            </button>
            {!!entries.length && <EntryList entries={entries} />}
          </section>
        )}

        {config.mode === "motor" && (
          <section style={styles.whiteCard}>
            <label style={styles.secondaryLabel}>
              {config.digitsLabel} <span style={styles.required}>*</span> <span style={styles.helperText}>{config.digitsHelper}</span>
              <input
                value={motorDigits}
                onChange={(event) => setMotorDigits(event.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                inputMode="numeric"
                placeholder="Enter digits (e.g., 1234)"
                style={styles.fullInput}
              />
            </label>

            <div style={styles.motorCount}>Unique digits entered: {new Set((motorDigits || "").replace(/\D/g, "").split("").filter(Boolean)).size}/10</div>

            {!!generatedMotorEntries.length && (
              <div style={styles.generatedMotorCard}>
                <div style={styles.generatedMotorTitle}>
                  Generated {variant === "dp_motor" ? "Double Patti Numbers" : "Single Patti Numbers"} ({generatedMotorEntries.length})
                </div>
                <div style={{ ...styles.generatedMotorGrid, gridTemplateColumns: isTiny ? "repeat(3, minmax(0, 1fr))" : isCompact ? "repeat(4, minmax(0, 1fr))" : styles.generatedMotorGrid.gridTemplateColumns }}>
                  {generatedMotorEntries.map((number) => (
                    <div key={number} style={styles.generatedMotorChip}>{number}</div>
                  ))}
                </div>
              </div>
            )}

            <label style={{ ...styles.secondaryLabel, marginTop: 8 }}>
              {config.amountLabel} <span style={styles.required}>*</span>
              <div style={styles.smallInputWrap}>
                <span style={styles.smallCurrency}>{"\u20B9"}</span>
                <input
                  value={motorAmount}
                  onChange={(event) => setMotorAmount(event.target.value.replace(/[^\d]/g, ""))}
                  inputMode="numeric"
                  placeholder="Enter amount"
                  style={{ ...styles.smallInput, paddingLeft: 42, textAlign: "left" }}
                />
              </div>
            </label>

            <button type="button" style={{ ...styles.addButton, background: theme.primary, marginTop: 16 }} onClick={addMotorEntries}>
              <Plus size={18} /> Add {generatedMotorEntries.length} {config.generatedLabel}
            </button>

            {!!entries.length && (
              <div style={styles.entryList}>
                {entries.map((entry, index) => (
                  <div key={`${entry.number}-${index}`} style={styles.entryRow}>
                    <span>{entry.number}</span>
                    <span>{`\u20B9${entry.amount}`}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <footer style={{ ...styles.footer, ...(isDesktop ? styles.footerDesktop : null), gridTemplateColumns: isCompact ? "1fr" : styles.footer.gridTemplateColumns, padding: isCompact ? "10px 12px calc(10px + env(safe-area-inset-bottom))" : isDesktop ? styles.footerDesktop.padding : styles.footer.padding }}>
        <div>
          <div style={styles.totalLabel}>Total Amount</div>
          <div style={{ ...styles.totalAmount, color: theme.total }}>{`\u20B9${effectiveTotal}`}</div>
        </div>
        <button
          type="button"
          disabled={!effectiveTotal || placing}
          style={{ ...styles.placeButton, width: "100%", background: !effectiveTotal || placing ? theme.primary : "linear-gradient(135deg, #22c55e, #16a34a)", opacity: !effectiveTotal || placing ? 0.55 : 1 }}
          onClick={placeBets}
        >
          {placing ? "Placing..." : "Place Bid"}
        </button>
      </footer>
      <MarketSheet
        open={marketSheetOpen}
        markets={marketOptions}
        selectedId={selectedMarket}
        onSelect={handleMarketChange}
        onClose={() => setMarketSheetOpen(false)}
      />
    </div>
  );
}

function EntryList({ entries }) {
  return (
    <div style={styles.entryList}>
      {entries.map((entry, index) => (
        <div key={`${entry.number}-${index}`} style={styles.entryRow}>
          <span>{entry.number}</span>
          <span>{`\u20B9${entry.amount}`}</span>
        </div>
      ))}
    </div>
  );
}

const SINGLE_PATTI_OPTIONS = [
  "127", "136", "145", "190", "235", "280", "370", "389", "460", "479", "569", "578",
  "128", "137", "146", "236", "245", "290", "380", "470", "489", "560", "579", "678",
  "129", "138", "147", "156", "237", "246", "345", "390", "480", "570", "589", "679",
  "120", "139", "148", "157", "238", "247", "256", "346", "490", "580", "670", "689",
  "130", "149", "158", "167", "239", "248", "257", "347", "356", "590", "680", "789",
  "140", "159", "168", "230", "249", "258", "267", "348", "357", "456", "690", "780",
  "123", "150", "169", "178", "240", "259", "268", "349", "358", "367", "457", "790",
  "124", "160", "278", "179", "250", "269", "340", "359", "368", "458", "467", "890",
  "125", "134", "170", "189", "260", "279", "350", "369", "468", "378", "459", "567",
  "126", "135", "180", "234", "270", "289", "360", "379", "450", "469", "478", "568",
];
const DOUBLE_PATTI_OPTIONS = [
  "118", "226", "244", "299", "334", "488", "550", "668", "677",
  "100", "119", "155", "227", "335", "344", "399", "588", "669",
  "110", "200", "228", "255", "336", "499", "660", "688", "778",
  "166", "229", "300", "337", "355", "445", "599", "779", "788",
  "112", "220", "266", "338", "400", "446", "455", "699", "770",
  "113", "122", "177", "339", "366", "447", "500", "799", "889", "555",
  "600", "114", "277", "330", "448", "466", "556", "880", "899",
  "115", "133", "188", "223", "377", "449", "557", "566", "700",
  "116", "224", "233", "288", "440", "477", "558", "800", "990",
  "117", "144", "199", "225", "388", "559", "577", "667", "900",
];
const TRIPLE_PATTI_OPTIONS = ["000", "111", "222", "333", "444", "555", "666", "777", "888", "999"];
const ALL_PANNA_OPTIONS = [...SINGLE_PATTI_OPTIONS, ...DOUBLE_PATTI_OPTIONS, ...TRIPLE_PATTI_OPTIONS];

function validatePattiValue(typePrefix, value) {
  const digits = String(value || "").split("");
  if (digits.length !== 3) return "";

  if (typePrefix === "sp") {
    return SINGLE_PATTI_OPTIONS.includes(value) ? "" : "Invalid single patti";
  }

  if (typePrefix === "dp") {
    return DOUBLE_PATTI_OPTIONS.includes(value) ? "" : "Invalid double patti";
  }

  const uniqueCount = new Set(digits).size;

  if (typePrefix === "tp" && uniqueCount !== 1) {
    return "Triple Patti only accepts all three digits same";
  }

  const canonicalError = validateGeneralPatti(value);
  if (canonicalError) return canonicalError;

  return "";
}

function validateGeneralPatti(value) {
  const raw = String(value || "");
  if (!/^\d{3}$/.test(raw)) return "";
  const normalized = raw.split("").sort().join("");
  if (raw !== normalized) {
    return `Invalid patti. Use ${normalized}`;
  }
  return "";
}

function validatePannaOption(value) {
  if (!/^\d{3}$/.test(String(value || ""))) return "";
  return ALL_PANNA_OPTIONS.includes(value) ? "" : "Invalid panna";
}

function getPannaSuggestions(value) {
  const query = String(value || "");
  if (!query) return [];
  return ALL_PANNA_OPTIONS.filter((option) => option.startsWith(query)).slice(0, 24);
}

function getSinglePattisForDigit(digit) {
  const target = Number(digit);
  return SINGLE_PATTI_OPTIONS.filter((option) =>
    option.split("").map(Number).reduce((sum, item) => sum + item, 0) % 10 === target
  );
}

function getDoublePattisForDigit(digit) {
  const target = Number(digit);
  return DOUBLE_PATTI_OPTIONS.filter((option) =>
    option.split("").map(Number).reduce((sum, item) => sum + item, 0) % 10 === target
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f9fafb",
    color: "#111827",
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    paddingTop: 58,
    paddingBottom: 132,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
  },
  topBar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    width: "100%",
    maxWidth: 540,
    height: 58,
    background: "linear-gradient(180deg, #fffdf8 0%, #fff7ed 100%)",
    display: "grid",
    gridTemplateColumns: "38px 1fr auto",
    alignItems: "center",
    gap: 10,
    padding: "4px 10px 0",
    borderBottom: "1px solid #fde7c2",
    boxShadow: "0 4px 14px rgba(146, 64, 14, 0.08)",
    boxSizing: "border-box",
  },
  topBarDesktop: {
    maxWidth: "100%",
    height: 72,
    padding: "6px max(24px, calc((100vw - 1440px) / 2)) 0",
    gridTemplateColumns: "48px 1fr auto",
  },
  headerLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  circleBack: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    border: "1px solid #e5e7eb",
    background: "linear-gradient(135deg, #fff7ed, #fffbeb)",
    color: "#92400e",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  title: {
    margin: 0,
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: "0.025em",
    color: "#92400e",
    textTransform: "uppercase",
  },
  datePill: {
    minWidth: 0,
    borderRadius: 999,
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.025em",
  },
  main: {
    width: "100%",
    maxWidth: 540,
    margin: "0 auto",
    padding: "12px 12px 0",
    boxSizing: "border-box",
  },
  mainDesktop: {
    maxWidth: 1440,
    padding: "24px 24px 0",
  },
  selectorCard: {
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 10,
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    boxSizing: "border-box",
  },
  selectorCardDesktop: {
    borderRadius: 18,
    padding: 18,
    gap: 16,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 700,
  },
  required: {
    color: "#ef4444",
  },
  selectWrap: {
    position: "relative",
    display: "block",
    marginTop: 8,
  },
  select: {
    width: "100%",
    height: 40,
    appearance: "none",
    borderWidth: 2,
    borderStyle: "solid",
    borderRadius: 10,
    background: "#fff",
    color: "#1f2937",
    padding: "0 30px 0 10px",
    fontSize: 10,
    fontWeight: 600,
    outline: "none",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  selectButton: {
    display: "flex",
    alignItems: "center",
    textAlign: "left",
    minHeight: 42,
    lineHeight: 1.25,
    whiteSpace: "normal",
    cursor: "pointer",
  },
  selectIcon: {
    position: "absolute",
    right: 8,
    top: 11,
    pointerEvents: "none",
  },
  whiteCard: {
    marginTop: 10,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 10,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    boxSizing: "border-box",
  },
  secondaryLabel: {
    display: "block",
    color: "#475569",
    fontSize: 10,
    fontWeight: 700,
  },
  helperText: {
    color: "#6b7280",
    fontWeight: 600,
  },
  largeInputWrap: {
    position: "relative",
    marginTop: 8,
  },
  currency: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 18,
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 18,
    fontWeight: 700,
    pointerEvents: "none",
  },
  largeInput: {
    width: "100%",
    height: 56,
    border: "2px solid #bfdbfe",
    borderRadius: 14,
    background: "#fff",
    textAlign: "center",
    fontSize: 17,
    fontWeight: 700,
    color: "#111827",
    outline: "2px solid #111827",
    outlineOffset: "-6px",
    boxSizing: "border-box",
  },
  bulkTitle: {
    fontSize: 10,
    fontWeight: 800,
    gridColumn: "1 / -1",
    marginBottom: 4,
  },
  ankBanner: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: 4,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
  },
  numberGrid: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 8,
  },
  redBracketGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 6,
    marginTop: 10,
  },
  selectAllRed: {
    width: "100%",
    minHeight: 48,
    borderRadius: 12,
    border: "1px solid #fecaca",
    background: "#fff5f5",
    color: "#991b1b",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 12px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  selectAllTick: {
    width: 22,
    height: 22,
    borderRadius: 4,
    border: "2px solid #9ca3af",
    background: "#fff",
    flexShrink: 0,
  },
  selectAllTickActive: {
    width: 22,
    height: 22,
    borderRadius: 4,
    border: "2px solid #16a34a",
    background: "#16a34a",
    boxShadow: "inset 0 0 0 3px #fff",
    flexShrink: 0,
  },
  jodiAmountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 12,
  },
  tripleAmountGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
  },
  jodiCell: {
    display: "grid",
    gap: 6,
  },
  jodiNumber: {
    height: 38,
    borderRadius: 9,
    background: "#cfe0fb",
    border: "1px solid #bfdbfe",
    display: "grid",
    placeItems: "center",
    color: "#1e3a8a",
    fontSize: 13,
    fontWeight: 800,
  },
  jodiAmountInput: {
    width: "100%",
    height: 40,
    minWidth: 0,
    borderRadius: 11,
    border: "2px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  tripleNumber: {
    height: 40,
    borderRadius: 9,
    background: "#ffe7cf",
    border: "1px solid #fdba74",
    display: "grid",
    placeItems: "center",
    color: "#9a3412",
    fontSize: 14,
    fontWeight: 800,
  },
  numberButton: {
    height: 42,
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  sangamBox: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    background: "#f8fafc",
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  sangamBoxDisabled: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    background: "#f3f4f6",
    opacity: 0.65,
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    pointerEvents: "none",
  },
  orLine: {
    margin: "16px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
  smallInputWrap: {
    position: "relative",
    marginTop: 8,
  },
  smallCurrency: {
    position: "absolute",
    left: 13,
    top: 12,
    color: "#9ca3af",
    fontSize: 14,
    fontWeight: 700,
  },
  smallInput: {
    width: "100%",
    height: 44,
    marginTop: 6,
    border: "1px solid #d1d5db",
    borderRadius: 11,
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },
  smallInputDisabled: {
    width: "100%",
    height: 44,
    marginTop: 6,
    border: "1px solid #d1d5db",
    borderRadius: 11,
    background: "#e5e7eb",
    color: "#94a3b8",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
    cursor: "not-allowed",
  },
  autoDigit: {
    marginTop: 8,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    textAlign: "center",
  },
  suggestionList: {
    marginTop: 10,
    maxHeight: 180,
    overflowY: "auto",
    border: "1px solid #dbeafe",
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
  },
  suggestionButton: {
    width: "100%",
    minHeight: 34,
    border: 0,
    borderBottom: "1px solid #eff6ff",
    background: "#fff",
    color: "#1e3a8a",
    textAlign: "left",
    padding: "0 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  noResultText: {
    marginTop: 10,
    color: "#dc2626",
    fontSize: 10,
    fontWeight: 700,
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 800,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },
  previewCard: {
    position: "relative",
    border: "1px solid #bfdbfe",
    borderRadius: 14,
    background: "#fff",
    minHeight: 58,
    display: "grid",
    placeItems: "center",
    padding: "10px 6px 8px",
  },
  previewRemove: {
    position: "absolute",
    top: -7,
    right: -7,
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: 0,
    background: "#ef4444",
    color: "#fff",
    fontSize: 14,
    lineHeight: 1,
    cursor: "pointer",
  },
  previewNumber: {
    color: "#2563eb",
    fontSize: 17,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  previewAmount: {
    color: "#475569",
    fontSize: 10,
    fontWeight: 700,
    marginTop: 4,
  },
  errorText: {
    marginTop: 8,
    color: "#dc2626",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  fullInput: {
    width: "100%",
    height: 48,
    marginTop: 8,
    border: "2px solid #bfdbfe",
    borderRadius: 12,
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },
  fullInputCompact: {
    width: "100%",
    height: 42,
    marginTop: 8,
    border: "1px solid #d1d5db",
    borderRadius: 10,
    background: "#fff",
    color: "#111827",
    textAlign: "center",
    fontSize: 14,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },
  motorCount: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 500,
    marginTop: 10,
  },
  generatedMotorCard: {
    marginTop: 16,
    border: "1px solid #fed7aa",
    borderRadius: 12,
    background: "#fff7ed",
    padding: 10,
  },
  generatedMotorTitle: {
    color: "#c2410c",
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 12,
  },
  generatedMotorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 8,
  },
  generatedMotorChip: {
    minHeight: 28,
    borderRadius: 8,
    background: "#ffedd5",
    color: "#c2410c",
    display: "grid",
    placeItems: "center",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 6px",
  },
  addButton: {
    width: "100%",
    height: 46,
    marginTop: 12,
    border: 0,
    borderRadius: 12,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
  },
  entryList: {
    marginTop: 12,
    display: "grid",
    gap: 8,
  },
  entryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 10px",
    borderRadius: 10,
    background: "#f8fafc",
    color: "#334155",
    fontSize: 12,
    fontWeight: 600,
  },
  footer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    background: "#fff",
    minHeight: 62,
    display: "grid",
    gridTemplateColumns: "1fr minmax(160px, 280px)",
    alignItems: "center",
    gap: 12,
    padding: "8px max(10px, calc((100vw - 540px) / 2))",
    borderTop: "2px solid #e5e7eb",
    boxShadow: "0 -12px 24px rgba(15, 23, 42, 0.12)",
    boxSizing: "border-box",
  },
  footerDesktop: {
    gridTemplateColumns: "1fr minmax(220px, 320px)",
    padding: "10px max(24px, calc((100vw - 1440px) / 2))",
  },
  totalLabel: {
    color: "#6b7280",
    fontSize: 10,
    fontWeight: 400,
  },
  totalAmount: {
    fontSize: 17,
    fontWeight: 900,
    marginTop: 2,
  },
  placeButton: {
    height: 46,
    border: 0,
    borderRadius: 12,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default GameBetPage;

